import omit from 'lodash/omit';
import {
  CurrencyAttributes,
  LevelAttributes,
} from '@loyalty/shared-common/gamification-api-client';
import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { EventName, EventSource } from '@loyalty/shared-common';
import { Fields } from '@react-awesome-query-builder/core';
import { ManagerIncomingWebhookAttributes } from '@loyalty/shared-common/manager-api-client';
import { binaryBasicFields } from './event-fields/binary-basic-fields';
import { ecommercePurchaseFields } from './event-fields/ecommerce-purchase-fields';
import { profileFields } from './event-fields/profile-fields';
import { qualifioAdvancedFormatPrediction } from './event-fields/qualifio-advanced-format-prediction';
import { qualifioEngageCampaign } from './event-fields/qualifio-engage-campaign';
import { qualifioLoyaltyBirthday } from './event-fields/qualifio-loyalty-birthday';
import { qualifioLoyaltyLevelChange } from './event-fields/qualifio-loyalty-level-change';
import { qualifioLoyaltyMemberClick } from './event-fields/qualifio-loyalty-member-click';
import { qualifioLoyaltyMemberVisit } from './event-fields/qualifio-loyalty-member-visit';
import { qualifioLoyaltyProfileCreation } from './event-fields/qualifio-loyalty-profile-creation';
import { sortObjectBy } from '../../../../shared/utils';

export interface BaseConfigSettings {
  eventSource?: EventSource;
  eventName?: EventName;
  levels?: LevelAttributes[];
  currencies?: CurrencyAttributes[];
  locale?: string;
  t: (translationKey: string) => string;
}

function getCommonFieldsConfiguration(
  settings: BaseConfigSettings,
  options?: { customKeys?: CustomKeyAttributes[]; webhooks?: ManagerIncomingWebhookAttributes[] },
): Fields {
  const customKeys = options?.customKeys;
  if (settings.eventSource === EventSource.ECOMMERCE && settings.eventName === EventName.PURCHASE) {
    return ecommercePurchaseFields(settings, customKeys);
  }

  if (settings.eventSource === EventSource.REFERRAL && settings.eventName === EventName.ECOMMERCE) {
    return {
      ...omit(ecommercePurchaseFields(settings, customKeys), ['event.subfields.referralCode']),
    };
  }

  if (
    settings.eventSource === EventSource.REFERRAL &&
    settings.eventName === EventName.PROFILE_CREATION
  ) {
    return {
      ...omit(qualifioLoyaltyProfileCreation(settings, customKeys), [
        'event.subfields.referralCode',
      ]),
    };
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_ENGAGE &&
    settings.eventName === EventName.CAMPAIGN
  ) {
    return qualifioEngageCampaign(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.ADVANCED_FORMAT &&
    settings.eventName === EventName.PREDICTION
  ) {
    return qualifioAdvancedFormatPrediction(settings, customKeys);
  }

  if (settings.eventSource === EventSource.BINARY) {
    let tags: string[] = [];
    let counters: string[] = [];
    if (settings?.eventName?.startsWith('webhook_')) {
      const webhook = options?.webhooks?.find(
        webhook => webhook.id === settings?.eventName?.replace('webhook_', ''),
      );
      tags = webhook?.config?.tags || [];
      counters = webhook?.config?.counters || [];
    }

    return binaryBasicFields(settings, { customKeys: options?.customKeys, tags, counters });
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.PROFILE_CREATION
  ) {
    return qualifioLoyaltyProfileCreation(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.LEVEL_CHANGE
  ) {
    return qualifioLoyaltyLevelChange(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.MEMBER_VISIT
  ) {
    return qualifioLoyaltyMemberVisit(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.MEMBER_CLICK
  ) {
    return qualifioLoyaltyMemberClick(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.BIRTHDAY
  ) {
    return qualifioLoyaltyBirthday(settings, customKeys);
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.BURNING
  ) {
    return {
      timestamp: {
        label: settings.t('rule.criteria.date'),
        type: 'datetime',
        preferWidgets: ['date'],
      },
      ...profileFields(settings, customKeys),
    };
  }

  if (
    settings.eventSource === EventSource.QUALIFIO_LOYALTY &&
    settings.eventName === EventName.ACCESS
  ) {
    return {
      ...profileFields(settings, customKeys),
    };
  }

  return {};
}

export function configureFieldsForBurningRules(
  settings: BaseConfigSettings,
  customKeys?: CustomKeyAttributes[],
): Fields {
  const fields: Fields = getCommonFieldsConfiguration(settings, { customKeys });
  return sortObjectBy(fields, ['campaign', 'optins', 'timestamp']);
}

export function configureFieldsForEarningRules(
  settings: BaseConfigSettings,
  options?: { customKeys?: CustomKeyAttributes[]; webhooks?: ManagerIncomingWebhookAttributes[] },
): Fields {
  const fields: Fields = getCommonFieldsConfiguration(settings, options);
  return sortObjectBy(fields, ['campaign', 'score', 'optins', 'timestamp']);
}

export function configureFieldsForAccessRules(
  settings: BaseConfigSettings,
  customKeys?: CustomKeyAttributes[],
): Fields {
  const fields: Fields = getCommonFieldsConfiguration(settings, { customKeys });
  return sortObjectBy(fields, ['campaign', 'optins', 'timestamp']); //TODO: whats this ?
}
